import type { Session } from '@supabase/supabase-js';
import { isNativePlatform } from '@/lib/platform';

const REMEMBER_KEY = 'rememberMe';
// Back-compat key used elsewhere in the app
const REMEMBER_KEY_LEGACY = 'lr_remember_me';

const PERSISTED_SESSION_KEY = 'lr_persisted_session_v1';

// 30-day "trusted device" cap on persisted Remember Me sessions.
// Rolled forward on every TOKEN_REFRESHED via persistSessionIfRemembered.
const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;

// Keys mirrored to @capacitor/preferences on native so they survive WebView
// data eviction / force-close. Kept in sync with localStorage; localStorage
// remains the primary read path so this module stays synchronous and
// AuthContext's boot flow doesn't have to change.
// NOTE: lr_device_id_v1 is included here so a native cold boot reuses the
// same device_id and re-registers the SAME (user_id, device_id) row via
// UPSERT — it must NOT look like a brand-new device (which would count
// against the 5-device cap and could evict the user's PC).
const NATIVE_MIRROR_KEYS = [REMEMBER_KEY, REMEMBER_KEY_LEGACY, PERSISTED_SESSION_KEY, 'lr_device_id_v1'] as const;

const mirrorToPreferences = (key: string, value: string | null): void => {
  if (!isNativePlatform()) return;
  // Fire-and-forget — never blocks the sync API. Retries once on failure
  // and logs LOUDLY if both attempts fail: this is the durable-storage
  // safety net for cold-boot session restore, so silent failures are a
  // regression waiting to happen.
  void (async () => {
    let Preferences: typeof import('@capacitor/preferences').Preferences | null = null;
    try {
      Preferences = (await import('@capacitor/preferences')).Preferences;
    } catch (e) {
      console.error('[remember-me] CRITICAL: failed to load @capacitor/preferences — Remember Me will not survive force-close:', e);
      return;
    }
    const write = async () => {
      if (value === null) {
        await Preferences!.remove({ key });
      } else {
        await Preferences!.set({ key, value });
      }
    };
    try {
      await write();
    } catch (e1) {
      console.warn('[remember-me] Preferences mirror failed, retrying once:', e1);
      try {
        await write();
      } catch (e2) {
        console.error('[remember-me] CRITICAL: Preferences mirror failed twice for key', key, '— cold boot may not restore session:', e2);
      }
    }
  })();
};

type PersistedSessionTokens = {
  access_token: string;
  refresh_token: string;
  saved_at: number;
  user_id?: string;
};

export const getRememberMePreference = (): boolean => {
  const v = localStorage.getItem(REMEMBER_KEY) ?? localStorage.getItem(REMEMBER_KEY_LEGACY);
  // Default to true for better UX (matches previous AuthPage behavior)
  return v !== 'false';
};

export const setRememberMePreference = (remember: boolean) => {
  const value = remember ? 'true' : 'false';
  localStorage.setItem(REMEMBER_KEY, value);
  localStorage.setItem(REMEMBER_KEY_LEGACY, value);
  mirrorToPreferences(REMEMBER_KEY, value);
  mirrorToPreferences(REMEMBER_KEY_LEGACY, value);
};

export const clearPersistedSession = () => {
  localStorage.removeItem(PERSISTED_SESSION_KEY);
  mirrorToPreferences(PERSISTED_SESSION_KEY, null);
};

export const persistSessionIfRemembered = (session: Session | null) => {
  if (!getRememberMePreference()) {
    clearPersistedSession();
    return;
  }
  if (!session?.access_token || !session?.refresh_token) return;

  const payload: PersistedSessionTokens = {
    access_token: session.access_token,
    refresh_token: session.refresh_token,
    saved_at: Date.now(),
    user_id: session.user?.id,
  };

  const serialized = JSON.stringify(payload);
  localStorage.setItem(PERSISTED_SESSION_KEY, serialized);
  mirrorToPreferences(PERSISTED_SESSION_KEY, serialized);
};

export const loadPersistedSessionTokens = (): { access_token: string; refresh_token: string } | null => {
  try {
    const raw = localStorage.getItem(PERSISTED_SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PersistedSessionTokens;
    if (!parsed?.access_token || !parsed?.refresh_token) return null;
    if (!parsed.saved_at || Date.now() - parsed.saved_at > MAX_AGE_MS) {
      clearPersistedSession();
      return null;
    }
    return { access_token: parsed.access_token, refresh_token: parsed.refresh_token };
  } catch {
    return null;
  }
};

// Native-only: called during nativeInit BEFORE React mounts. Copies mirrored
// Remember Me values from @capacitor/preferences back into WebView
// localStorage so the synchronous read path above finds them even after
// Android has wiped the WebView's storage on force-close. Additive only —
// never overwrites an existing localStorage value.
export const primeRememberMeFromNativeStore = async (): Promise<void> => {
  if (!isNativePlatform()) return;
  try {
    const { Preferences } = await import('@capacitor/preferences');
    for (const key of NATIVE_MIRROR_KEYS) {
      try {
        const existing = localStorage.getItem(key);
        if (existing !== null) continue;
        const { value } = await Preferences.get({ key });
        if (value !== null && value !== undefined) {
          localStorage.setItem(key, value);
        }
      } catch { /* per-key failure is non-fatal */ }
    }
  } catch (e) {
    console.warn('[remember-me] prime from Preferences failed:', e);
  }
};
